import React, { useEffect } from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import {
  User,
  LogOut,
  HelpCircle,
  Palette,
  RefreshCw,
  UserCog
} from "lucide-react"
import { useAuth } from "../auth/AuthContext"
import { reloadAppConfig } from "../acl/reloadAppConfig"
import { exitImpersonation, getImpersonatorUser, hasStoredImpersonationSession } from "../auth/impersonateUser"
import { useAppVersion } from "@/hooks/useAppVersion"
import { useLocation } from "react-router-dom"

function ProfileMenu({ displayText, onMenuClick, resetMenuTrigger, children }: { displayText: any, onMenuClick?: any, resetMenuTrigger?: any, children?: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isExitingImpersonation, setIsExitingImpersonation] = useState(false);
  const { user: authUser } = useAuth()
  const reduxUser = useSelector((state: any) => state.user)
  const user = authUser || reduxUser
  // Read storage only — isImpersonating() validates uids and must not run on every render.
  const impersonating = hasStoredImpersonationSession();
  const impersonatorUser = impersonating ? getImpersonatorUser() : null;
  const impersonatorLabel =
    [impersonatorUser?.info?.firstName || impersonatorUser?.firstName, impersonatorUser?.info?.lastName || impersonatorUser?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    impersonatorUser?.email ||
    "admin account";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const tableMeta = useSelector((state: any) => state.table.meta ?? [])
  const showSupportSection = false
  const { version: appVersion, releaseDate: appReleaseDate } = useAppVersion()

  const userUid = user?.uid || user?.info?.uid || user?.userData?.uid || null
  const isAdmin = Boolean(user?.admin || user?.info?.admin)

  const getFullName = (): string | null => {
    const firstName = user?.info?.firstName || user?.firstName
    const lastName = user?.info?.lastName || user?.lastName
    if (firstName || lastName) {
      return [firstName, lastName].filter(Boolean).join(" ").trim()
    }
    return null
  }

  const fullName = getFullName()
  const emailFromUser = typeof user?.email === "string" ? user.email : ""
  const emailFromProp = typeof displayText === "string" ? displayText : ""
  const email = emailFromUser || emailFromProp || "user@example.com"
  const headerName =
    fullName ||
    (email.includes("@") ? email.split("@")[0] : email) ||
    "User"

  const logout = () => {
    navigate('/login', { replace: true })
    dispatch({ type: 'LOGOUT' })
    localStorage.removeItem('persist:root')
  }

  const handleReloadConfig = async () => {
    if (!user) {
      console.warn('No user available for reloading components')
      return
    }

    setIsReloading(true)
    try {
      reloadAppConfig(dispatch, user, {
        reloadCurrentCollection: true,
        pathname: location.pathname,
        tableMeta,
      })
      await new Promise(resolve => setTimeout(resolve, 500))
    } catch (error) {
      console.error('Failed to reload component configurations:', error)
    } finally {
      setIsReloading(false)
    }
  }

  const handleExitImpersonation = async () => {
    setIsExitingImpersonation(true)
    try {
      await exitImpersonation()
    } catch (error) {
      console.error('Failed to exit impersonation:', error)
      setIsExitingImpersonation(false)
    }
  }

  useEffect(() => {
    if (onMenuClick && resetMenuTrigger) {
      resetMenuTrigger()
      setIsOpen(!isOpen)
    }
  }, [onMenuClick])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        {children || <div style={{ display: 'none' }} />}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        side="bottom"
        className="z-[100] w-72 bg-popover text-popover-foreground border border-gray-200 dark:border-gray-600 shadow-xl rounded-lg p-1.5 backdrop-blur-sm"
        sideOffset={8}
      >
        {/* User Info Header */}
        <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 mb-1">
          <div className="flex items-center space-x-2">
            <div className="flex-1">
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {headerName}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                {email}
              </div>
              {isAdmin && userUid && (
                <div className="text-xs text-muted-foreground font-mono mt-0.5">
                  UID: {userUid}
                </div>
              )}
              <div className="flex items-center space-x-2 mt-1">
                <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200 text-xs">
                  Online
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {impersonating && (
          <>
            <div className="px-3 py-1.5 mb-1 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 text-[11px] text-amber-800 dark:text-amber-200">
              Impersonating this user. Return to {impersonatorLabel} when finished.
            </div>
            <DropdownMenuItem
              onClick={(e) => { e.preventDefault(); handleExitImpersonation(); }}
              disabled={isExitingImpersonation}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm hover:bg-amber-50 dark:hover:bg-amber-950/50 hover:text-amber-900 dark:hover:text-amber-100 rounded-md cursor-pointer transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-7 h-7 bg-amber-100 dark:bg-amber-950/80 rounded-md flex items-center justify-center group-hover:bg-amber-200 dark:group-hover:bg-amber-900/60 transition-colors">
                <UserCog className={`h-3.5 w-3.5 text-amber-600 dark:text-amber-400 group-hover:text-amber-700 dark:group-hover:text-amber-300 ${isExitingImpersonation ? 'animate-pulse' : ''}`} />
              </div>
              <div className="flex-1">
                <span className="font-medium">Exit Impersonation</span>
                <div className="text-xs text-muted-foreground">Back to your admin account</div>
              </div>
            </DropdownMenuItem>

            <DropdownMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />
          </>
        )}

        <DropdownMenuLabel className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
          System
        </DropdownMenuLabel>

        <DropdownMenuItem
          onClick={() => { setIsOpen(false); handleReloadConfig(); }}
          disabled={isReloading}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm hover:bg-orange-50 dark:hover:bg-orange-950/50 hover:text-orange-900 dark:hover:text-orange-100 rounded-md cursor-pointer transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="w-7 h-7 bg-orange-100 dark:bg-orange-950/80 rounded-md flex items-center justify-center group-hover:bg-orange-200 dark:group-hover:bg-orange-900/60 transition-colors">
            <RefreshCw className={`h-3.5 w-3.5 text-orange-600 dark:text-orange-400 group-hover:text-orange-700 dark:group-hover:text-orange-300 ${isReloading ? 'animate-spin' : ''}`} />
          </div>
          <div className="flex-1">
            <span className="font-medium">Reload Config</span>
            <div className="text-xs text-muted-foreground">Collections, table cache, permissions, submissions</div>
          </div>
        </DropdownMenuItem>

        {showSupportSection && (
          <>
            <DropdownMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />

            <DropdownMenuLabel className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
              Support
            </DropdownMenuLabel>

            <DropdownMenuItem onClick={() => setIsOpen(false)} className="flex items-center space-x-2 px-3 py-1.5 text-sm hover:bg-green-50 dark:hover:bg-green-950/50 hover:text-green-900 dark:hover:text-green-100 rounded-md cursor-pointer transition-all duration-200 group">
              <div className="w-7 h-7 bg-green-100 dark:bg-green-950/80 rounded-md flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/60 transition-colors">
                <HelpCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 group-hover:text-green-700 dark:group-hover:text-green-300" />
              </div>
              <div className="flex-1">
                <span className="font-medium">Help & Support</span>
                <div className="text-xs text-muted-foreground">Get assistance</div>
              </div>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => setIsOpen(false)} className="flex items-center space-x-2 px-3 py-1.5 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-950/50 hover:text-indigo-900 dark:hover:text-indigo-100 rounded-md cursor-pointer transition-all duration-200 group">
              <div className="w-7 h-7 bg-indigo-100 dark:bg-indigo-950/80 rounded-md flex items-center justify-center group-hover:bg-indigo-200 dark:group-hover:bg-indigo-900/60 transition-colors">
                <Palette className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300" />
              </div>
              <div className="flex-1">
                <span className="font-medium">Appearance</span>
                <div className="text-xs text-muted-foreground">Theme & display</div>
              </div>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />

        <DropdownMenuItem
          onClick={() => { navigate('/profile'); setIsOpen(false); }}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm hover:bg-blue-50 dark:hover:bg-blue-950/50 hover:text-blue-900 dark:hover:text-blue-100 rounded-md cursor-pointer transition-all duration-200 group"
        >
          <div className="w-7 h-7 bg-blue-100 dark:bg-blue-950/80 rounded-md flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60 transition-colors">
            <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
          </div>
          <div className="flex-1">
            <span className="font-medium">Profile</span>
            <div className="text-xs text-muted-foreground">Update your details</div>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1 border-gray-100 dark:border-gray-700" />

        <DropdownMenuItem
          onClick={() => { setIsOpen(false); logout(); }}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-900 dark:hover:text-red-300 rounded-md cursor-pointer transition-all duration-200 group"
        >
          <div className="w-7 h-7 bg-red-100 dark:bg-red-950/80 rounded-md flex items-center justify-center group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-colors">
            <LogOut className="h-3.5 w-3.5 text-red-600 dark:text-red-400 group-hover:text-red-700 dark:group-hover:text-red-300" />
          </div>
          <div className="flex-1">
            <span className="font-medium">Sign Out</span>
            <div className="text-xs text-red-400 dark:text-red-400/90">End your session</div>
          </div>
        </DropdownMenuItem>

        {/* Footer */}
        <div className="px-3 py-2 mt-1 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              {appVersion
                ? `v ${appVersion}${appReleaseDate ? ` · ${appReleaseDate}` : ""}`
                : ""}
            </span>
            <span>© 2026 Labwick</span>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default ProfileMenu